import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import SingleCardTemplate from '@/components/layouts/SingleCardTemplate';
import Loading from '@/components/ui/loading/Loading';
import { pageTitles } from '@/constants/pageTitles';
import { PARENTS_LIMIT_EXCEEDED_ERROR_CODE } from '@/features/invitations/constants';
import useReactQuery from '@/hooks/common/useReactQuery';
import useCurrentUser from '@/hooks/store/useCurrentUser';

import InviteEmailConfirmForm from './InviteEmailConfirmForm';

type LocationState = {
  email: string;
};

const InviteEmailConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { code } = useParams<{ code: string }>();
  const [currentUser] = useCurrentUser();
  const { useMutation } = useReactQuery();

  const state = location.state as LocationState | null;
  const email = state?.email;

  const student = useMemo(() => {
    if (!code || !currentUser) {
      return null;
    }
    return (
      currentUser.organization_memberships.find((s) => s.code === code) ?? null
    );
  }, [code, currentUser]);

  const { mutate, isPending, error } = useMutation({
    method: 'POST',
    url: '/api/school/parents/invitations',
  });

  const apiError =
    error && error.status !== 400 && error.status !== 422
      ? '予期しないエラーが発生しました。時間をおいて再度お試しください'
      : undefined;

  const handleBack = () => {
    navigate(`/mypage/invite/${code}`, {
      state: { email },
    });
  };

  const handleSubmit = () => {
    mutate(
      JSON.stringify({
        email,
        organization_membership_id: student?.id ?? '',
      }),
      {
        onSuccess: () => {
          navigate('/mypage/invite/complete');
        },
        onError: (err) => {
          if (err.status === 400) {
            navigate(`/mypage/invite/${code}/email-error`, {
              state: { email },
            });
          } else if (err.error_code === PARENTS_LIMIT_EXCEEDED_ERROR_CODE) {
            navigate('/mypage/invite/limit-error');
          } else if (err.status === 422) {
            navigate('/mypage/invite/inquiry');
          }
        },
      },
    );
  };

  if (!currentUser) {
    return (
      <SingleCardTemplate title={pageTitles.inviteEmailConfirm}>
        <Loading />
      </SingleCardTemplate>
    );
  }

  if (!code || !student || !email) {
    return <ErrorPage type="not_found" />;
  }

  return (
    <SingleCardTemplate title={pageTitles.inviteEmailConfirm}>
      <Helmet>
        <title>{pageTitles.inviteEmailConfirm}</title>
      </Helmet>
      <InviteEmailConfirmForm
        student={student}
        email={email}
        onBack={handleBack}
        onSubmit={handleSubmit}
        isLoading={isPending}
        apiError={apiError}
      />
    </SingleCardTemplate>
  );
};

export default InviteEmailConfirmPage;
